import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ErrorState, Pagination } from '@/components/common';
import { formatDateTime } from '@/shared/lib/format';
import { releasePath } from '@/constants';
import { useReleases } from './queries';
import { PLATFORM_LABEL, type ReleaseListQuery, type Release } from './types';

const PAGE_SIZE = 20;
const ANY = 'any';

/** draft is inert, released positive, withdrawn negative - the pill's own words. */
const STATUS_TONE = { draft: 'inert', released: 'positive', withdrawn: 'negative' } as const;

/**
 * Every release, newest first.
 *
 * A release is a version number on a channel; its Windows and Linux builds
 * are listed in the Builds column, each with whether it is code-signed - the
 * one property most likely to stop an install on a customer's machine.
 * Opening a row is where it is edited, released, withdrawn and announced.
 */
export function ReleaseTable() {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<string>(ANY);
  const [status, setStatus] = useState<string>(ANY);
  const [offset, setOffset] = useState(0);

  const query: ReleaseListQuery = {
    limit: PAGE_SIZE,
    offset,
    ...(channel !== ANY ? { channel: channel as ReleaseListQuery['channel'] } : {}),
    ...(status !== ANY ? { status: status as ReleaseListQuery['status'] } : {}),
  };
  const releases = useReleases(query);

  const filter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setOffset(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={status} onValueChange={filter(setStatus)}>
          <SelectTrigger className="w-40" aria-label="Filter by status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="released">Released</SelectItem>
            <SelectItem value="withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>
        <Select value={channel} onValueChange={filter(setChannel)}>
          <SelectTrigger className="w-40" aria-label="Filter by channel">
            <SelectValue placeholder="Channel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All channels</SelectItem>
            <SelectItem value="stable">Stable</SelectItem>
            <SelectItem value="beta">Beta</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {releases.isError && (
        <ErrorState
          title="Could not load releases"
          description={releases.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void releases.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Version</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="hidden md:table-cell w-52">Builds</TableHead>
              <TableHead className="hidden lg:table-cell w-24">Required</TableHead>
              <TableHead className="w-48">Released</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {releases.isPending &&
              Array.from({ length: 5 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {releases.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No releases match these filters. CI creates one when it registers a build.
                </TableCell>
              </TableRow>
            )}

            {releases.data?.data.map((release) => (
              <TableRow
                key={release.id}
                className="cursor-pointer"
                onClick={() => {
                  void navigate(releasePath(release.id));
                }}
              >
                <TableCell>
                  <Link
                    to={releasePath(release.id)}
                    className="font-mono font-medium hover:underline"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {release.versionNumber}
                  </Link>
                  <div className="mt-1">
                    <StatusPill status={release.releaseChannel} minWidth={56} />
                  </div>
                </TableCell>
                <TableCell className="max-w-md">
                  <p className={release.title ? 'font-medium' : 'text-muted-foreground'}>
                    {release.title ?? 'No title yet'}
                  </p>
                  {release.summary && (
                    <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                      {release.summary}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <StatusPill status={release.status} tone={STATUS_TONE[release.status]} />
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <Builds release={release} />
                </TableCell>
                <TableCell className="hidden lg:table-cell text-muted-foreground">
                  {release.mandatory
                    ? 'Yes'
                    : release.minSupportedVersion
                      ? `< ${release.minSupportedVersion}`
                      : 'No'}
                </TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {release.releasedAt ? (
                    <>
                      {formatDateTime(release.releasedAt)}
                      {release.releasedBy && (
                        <span className="block text-xs">by {release.releasedBy.name}</span>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>

      {releases.data && (
        <Pagination
          offset={offset}
          pageSize={PAGE_SIZE}
          count={releases.data.data.length}
          total={releases.data.meta.total}
          hasMore={releases.data.meta.hasMore}
          onOffsetChange={setOffset}
        />
      )}
    </div>
  );
}

function Builds({ release }: { release: Release }) {
  if (release.builds.length === 0) {
    return <span className="text-sm text-muted-foreground">None yet</span>;
  }
  return (
    <ul className="space-y-0.5 text-sm">
      {release.builds.map((build) => (
        <li key={build.id} className="flex items-center gap-1.5">
          {build.codeSigned ? (
            <ShieldCheck size={14} className="text-pill-positive-foreground" aria-label="Signed" />
          ) : (
            <ShieldAlert
              size={14}
              className="text-pill-caution-foreground"
              aria-label="Not signed"
            />
          )}
          {PLATFORM_LABEL[build.platform]}
          {!build.codeSigned && <span className="text-xs text-muted-foreground">unsigned</span>}
        </li>
      ))}
    </ul>
  );
}
